import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../api';
import PageLayout from '../components/PageLayout';
import { formatDateTime } from '../utils/formatDate';
import TablePagination from '../components/TablePagination';
import TableSearch from '../components/TableSearch';

const ManagementLogs = () => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalCount: 0
    });

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchTerm.trim());
            setPagination((prev) => ({ ...prev, currentPage: 1 }));
        }, 300);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    useEffect(() => {
        const fetchLogs = async () => {
            try {
                setLoading(true);
                const response = await api.get('/api/admin/logs/scans', {
                    params: {
                        page: pagination.currentPage,
                        limit: 50,
                        search: debouncedSearch
                    }
                });

                if (response.data.success) {
                    setLogs(response.data.data);
                    if (response.data.pagination) {
                        setPagination(prev => ({
                            ...prev,
                            totalPages: response.data.pagination.totalPages,
                            totalCount: response.data.pagination.totalCount
                        }));
                    }
                }
            } catch (error) {
                console.error('Error fetching logs:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchLogs();
    }, [pagination.currentPage, debouncedSearch]);

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.totalPages) {
            setPagination(prev => ({ ...prev, currentPage: newPage }));
        }
    };

    const formatDate = (dateString) => formatDateTime(dateString);

    const getGradeBadgeClass = (grade) => {
        switch (grade) {
            case 'A': return 'badge bg-success';
            case 'B': return 'badge bg-info';
            case 'C': return 'badge bg-warning';
            case 'D': return 'badge bg-warning';
            case 'F': return 'badge bg-danger';
            default: return 'badge bg-secondary';
        }
    };

    return (
        <PageLayout title="System Scan Logs">
            <div className="row mb-4">
                <div className="col">
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <h2 className="h4 mb-1">
                                <i className="las la-clipboard-list me-2 text-info"></i>
                                System Scan Logs
                            </h2>
                            <p className="text-muted mb-0">View all system scan activity</p>
                        </div>
                        <div>
                            <Link to="/management/dashboard" className="btn btn-outline-secondary">
                                <i className="las la-arrow-left me-2"></i>
                                Back to Dashboard
                            </Link>
                        </div>
                    </div>
                </div>
            </div>

            {/* Search Bar */}
            <div className="row mb-4 align-items-center">
                <div className="col-md-6">
                    <TableSearch
                        value={searchTerm}
                        onChange={setSearchTerm}
                        placeholder="Search by domain or risk level…"
                    />
                </div>
                <div className="col-md-6 text-end">
                    <span className="badge bg-light text-dark border">
                        Total Scans: {pagination.totalCount}
                    </span>
                </div>
            </div>

            <div className="card shadow-sm">
                <div className="card-body p-0">
                    <div className="table-responsive">
                        <table className="table table-hover mb-0">
                            <thead className="table-light">
                                <tr>
                                    <th className="ps-4">Domain</th>
                                    <th>Grade</th>
                                    <th>Score</th>
                                    <th>Risk Level</th>
                                    <th>Scan Time</th>
                                    <th className="text-end pe-4">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan="6" className="text-center py-5">
                                            <div className="spinner-border text-primary" role="status">
                                                <span className="visually-hidden">Loading...</span>
                                            </div>
                                        </td>
                                    </tr>
                                ) : logs.length === 0 ? (
                                    <tr>
                                        <td colSpan="6" className="text-center py-5">
                                            <p className="text-muted mb-0">No scans found.</p>
                                        </td>
                                    </tr>
                                ) : (
                                    logs.map((log) => (
                                        <tr key={log.id}>
                                            <td className="ps-4">
                                                <strong>{log.domain}</strong>
                                            </td>
                                            <td>
                                                <span className={getGradeBadgeClass(log.security_grade)}>
                                                    {log.security_grade}
                                                </span>
                                            </td>
                                            <td>{log.security_score}</td>
                                            <td>{log.risk_level}</td>
                                            <td>{formatDate(log.created_at)}</td>
                                            <td className="text-end pe-4">
                                                <a
                                                    href={`/report/${log.download_token}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="btn btn-sm btn-outline-primary"
                                                >
                                                    <i className="las la-external-link-alt me-1"></i>
                                                    View Report
                                                </a>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <TablePagination
                className="mt-3"
                currentPage={pagination.currentPage}
                totalPages={pagination.totalPages}
                totalCount={pagination.totalCount}
                showingFrom={pagination.totalCount === 0 ? 0 : (pagination.currentPage - 1) * 50 + 1}
                showingTo={Math.min(pagination.currentPage * 50, pagination.totalCount)}
                onPageChange={handlePageChange}
            />
        </PageLayout>
    );
};

export default ManagementLogs;
